// frontend/src/features/adhesives/AdhesiveResult.tsx
import React, { useState } from "react";
import { Download, CheckCircle, AlertTriangle, Shield, Clock, Info, FileText } from "lucide-react";
import { generateAdhesivePDF } from "./generatePDF";

export function AdhesiveResult({ data }: { data: any }) {
  const { input, result, adhesive, process, warnings } = data;
  const [pdfGenerating, setPdfGenerating] = useState<string | null>(null);

  const generatePDF = (mode: "client" | "production") => {
    setPdfGenerating(mode);
    
    try {
      // Generowanie PDF po stronie klienta
      generateAdhesivePDF(data, mode);
      
      // Sukces - pokazujemy powiadomienie
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
      notification.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        PDF wygenerowany i pobrany!
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } catch (error: any) {
      console.error('Błąd generowania PDF:', error);
      
      // Błąd - pokazujemy powiadomienie
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
      notification.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        Błąd generowania PDF
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } finally {
      setPdfGenerating(null);
    }
  };

  const isOk = result.ok && result.utilization <= 1.0;
  const utilizationPercent = (result.utilization * 100).toFixed(0);
  const utilizationColor = result.utilization > 0.8 ? 'text-red-500' : result.utilization > 0.5 ? 'text-yellow-500' : 'text-green-500';

  return (
    <div className="space-y-6">
      {/* Status główny */}
      <div className={`rounded-xl p-6 ${isOk ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-start gap-4">
          {isOk ? (
            <CheckCircle className="w-8 h-8 text-green-500 mt-1" />
          ) : (
            <AlertTriangle className="w-8 h-8 text-red-500 mt-1" />
          )}
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-2">
              {isOk ? 'Połączenie spełnia wymagania' : 'Połączenie nie spełnia wymagań'}
            </h2>
            <div className="text-sm space-y-1 text-zinc-300">
              <p>
                Naprężenie ścinające: <span className="font-bold text-white">{result.tau_MPa.toFixed(3)} MPa</span>
                {' '}(limit: {result.tau_allow_MPa} MPa)
              </p>
              <p>
                Wykorzystanie spoiny: <span className={`font-bold ${utilizationColor}`}>{utilizationPercent}%</span>
              </p>
              <p className="text-zinc-400">
                Pole spoiny: {result.area_mm2} mm² • Zalecane (≤50% τ_dop): {result.min_area_mm2_for_FS05.toFixed(0)} mm²
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ostrzeżenia z walidacji kompatybilności */}
      {warnings?.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2 text-yellow-500">
            <AlertTriangle className="w-5 h-5" />
            Ostrzeżenia dotyczące kompatybilności
          </h3>
          <ul className="list-disc ml-7 text-sm text-yellow-400 space-y-1">
            {warnings.map((w: string, i: number) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Ostrzeżenia z obliczeń */}
      {result.warnings?.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2 text-orange-500">
            <AlertTriangle className="w-5 h-5" />
            Ostrzeżenia techniczne
          </h3>
          <ul className="list-disc ml-7 text-sm text-orange-400 space-y-1">
            {result.warnings.map((w: string, i: number) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Informacje o kleju */}
      <div className="bg-zinc-800 rounded-xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-500" />
          Zalecany klej
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-zinc-400">Nazwa:</span>
            <p className="font-semibold">{adhesive.name}</p>
          </div>
          <div>
            <span className="text-zinc-400">Klasa:</span>
            <p className="font-semibold">
              {adhesive.class}
              {adhesive.subclass && ` (${adhesive.subclass})`}
            </p>
          </div>
          <div>
            <span className="text-zinc-400">Producent:</span>
            <p className="font-semibold">{adhesive.vendor}</p>
          </div>
          <div>
            <span className="text-zinc-400">Lepkość:</span>
            <p className="font-semibold">{adhesive.viscosity_mPas || "—"} mPas</p>
          </div>
          <div>
            <span className="text-zinc-400">Szczelina:</span>
            <p className="font-semibold">{adhesive.gap_mm[0]}–{adhesive.gap_mm[1]} mm</p>
          </div>
          <div>
            <span className="text-zinc-400">Cena orientacyjna:</span>
            <p className="font-semibold">{adhesive.price_PLN || "—"}</p>
          </div>
          {adhesive.notes?.[0]?.includes("Zamiennik PL:") && (
            <div className="col-span-2 border-t border-zinc-700 pt-3 mt-2">
              <span className="text-zinc-400">Uwaga:</span>
              <p className="font-semibold text-blue-400">{adhesive.notes[0]}</p>
            </div>
          )}
        </div>
      </div>

      {/* Proces klejenia */}
      <div className="bg-zinc-800 rounded-xl p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Proces klejenia
        </h3>
        <ol className="space-y-2">
          {process.steps.map((s: string, i: number) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="flex-shrink-0 w-6 h-6 bg-indigo-500/20 text-indigo-500 rounded-full flex items-center justify-center text-xs font-bold">
                {i + 1}
              </span>
              <span className="text-zinc-300">{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* BHP */}
      {process.bhp?.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-yellow-500">
            <Shield className="w-5 h-5" />
            Wymogi BHP
          </h3>
          <ul className="space-y-2">
            {process.bhp.map((s: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-yellow-500 mt-1">•</span>
                <span className="text-yellow-400">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Przyciski PDF */}
      <div className="flex gap-3">
        <button 
          className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-zinc-600 text-white rounded-lg px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2"
          onClick={() => generatePDF("client")}
          disabled={pdfGenerating !== null}
        >
          {pdfGenerating === 'client' ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Generowanie...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5" />
              PDF dla klienta
            </>
          )}
        </button>
        <button 
          className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-zinc-600 text-white rounded-lg px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2"
          onClick={() => generatePDF("production")}
          disabled={pdfGenerating !== null}
        >
          {pdfGenerating === 'production' ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Generowanie...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              PDF produkcyjny
            </>
          )}
        </button>
      </div>
    </div>
  );
}
